import { NavigationComponent, UserMenuComponent, NotificationBellComponent } from '@/app/core/components';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink, RouterOutlet } from '@angular/router';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Store } from '@ngrx/store';
import { AuthActions, AuthSelectors } from '@/app/+state/auth';
import { AsyncPipe } from '@angular/common';
import { UserRole } from '@qc/shared';
import { take, filter } from 'rxjs';
import { SidebarService, TokenStorageService } from './core/services';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { NotificationWebSocketService } from '@/app/core/services/notification-websocket.service';
import { Notification, NotificationType } from '@/app/shared/interfaces/notification.interface';
import { PasswordSessionService } from './core/services/password-session.service';
import { SessionActivityService } from './core/services/session-activity.service';
import { TicketsWidgetService } from './core/services/tickets-widget.service';

@Component({
    selector: 'qc-root',
    standalone: true,
    imports: [
        CommonModule,
        RouterOutlet,
        AsyncPipe,
        RouterLink,
        NavigationComponent,
        NgOptimizedImage,
        UserMenuComponent,
        NotificationBellComponent,
        ToastModule,
    ],
    template: `
        <ng-container *ngIf="appInitialized$ | async; else loading">
            <ng-container *ngIf="user$ | async as user; else unauthenticated">
                <div class="qc-app-shell">
                    <aside
                        class="qc-nav-rail"
                        [class.expanded]="!sidebar.collapsed()">
                        <a routerLink="/" class="qc-nav-logo" title="Q&C Services">
                            <span *ngIf="sidebar.collapsed(); else fullLogo">Q</span>
                            <ng-template #fullLogo>
                                <img
                                    ngSrc="logo-horizontal-reverse-tall.png"
                                    width="200"
                                    height="112"
                                    alt="Q&C Services"
                                    priority/>
                            </ng-template>
                        </a>

                        <qc-navigation class="qc-nav-items" (closeMenu)="closeMobileMenu()"></qc-navigation>

                        <div class="qc-nav-bottom">
                            <button
                                class="qc-nav-toggle"
                                (click)="sidebar.toggle()"
                                [title]="sidebar.collapsed() ? 'Expand sidebar' : 'Collapse sidebar'"
                                [attr.aria-label]="sidebar.collapsed() ? 'Expand sidebar' : 'Collapse sidebar'">
                                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" width="16" height="16">
                                    <path *ngIf="!sidebar.collapsed()" d="M10 4l-4 4 4 4" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path *ngIf="sidebar.collapsed()" d="M6 4l4 4-4 4" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </button>
                            <qc-user-menu class="qc-nav-user"></qc-user-menu>
                        </div>
                    </aside>

                    <main class="qc-app-main">
                        <router-outlet />
                    </main>
                </div>
            </ng-container>

            <ng-template #unauthenticated>
                <router-outlet />
            </ng-template>
        </ng-container>

        <ng-template #loading>
            <div class="fixed inset-0 flex items-center justify-center bg-white">
                <div class="text-center">
                    <div
                        class="w-16 h-16 border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin mx-auto"
                    ></div>
                    <p class="mt-4 text-gray-700">Loading...</p>
                </div>
            </div>
        </ng-template>

        <p-toast key="global" position="bottom-right" />
    `,
    styles: [`
        :host { display: block; height: 100vh; overflow: hidden; }
        .qc-app-shell {
            display: grid;
            grid-template-columns: 72px minmax(0, 1fr);
            height: 100vh;
            min-height: 600px;
            background: #f6f7f5;
            transition: grid-template-columns 180ms ease;
        }
        .qc-app-shell:has(.qc-nav-rail.expanded) { grid-template-columns: 240px minmax(0, 1fr); }

        .qc-nav-rail {
            background: linear-gradient(180deg, #1f7a3a 0%, #176030 100%);
            color: #e9f3ec;
            display: flex;
            flex-direction: column;
            padding: 14px 0 14px;
            overflow: hidden;
            position: relative;
        }

        .qc-nav-logo {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 44px;
            height: 44px;
            margin: 2px auto 14px;
            background: rgba(255, 255, 255, 0.08);
            border-radius: 10px;
            color: #ffffff;
            font-weight: 700;
            font-size: 18px;
            text-decoration: none;
        }
        .qc-nav-rail.expanded .qc-nav-logo {
            width: auto;
            height: 80px;
            margin: 4px 16px 18px;
            background: transparent;
            justify-content: center;
        }
        .qc-nav-logo img { display: block; max-height: 64px; width: auto; }

        .qc-nav-items {
            flex: 1;
            min-height: 0;
            overflow-y: auto;
        }

        .qc-nav-bottom {
            border-top: 1px solid rgba(255, 255, 255, 0.08);
            padding: 8px 0 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
        }

        .qc-nav-toggle {
            background: transparent;
            border: 0;
            color: rgba(255, 255, 255, 0.7);
            width: 36px;
            height: 36px;
            border-radius: 8px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .qc-nav-toggle:hover { background: rgba(255, 255, 255, 0.08); color: #ffffff; }

        .qc-nav-bell {
            width: 36px;
            height: 36px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #e9f3ec;
        }
        .qc-nav-bell ::ng-deep .pi { color: #e9f3ec !important; }
        .qc-nav-bell:hover { background: rgba(255, 255, 255, 0.08); }

        .qc-nav-user { width: 100%; }
        .qc-app-shell:not(:has(.qc-nav-rail.expanded)) .qc-nav-user ::ng-deep .text-white,
        .qc-app-shell:not(:has(.qc-nav-rail.expanded)) .qc-nav-user ::ng-deep button > div:not(:first-child) {
            display: none;
        }

        .qc-app-main {
            min-width: 0;
            min-height: 0;
            overflow: auto;
            display: flex;
            flex-direction: column;
        }
    `],
})
export class AppComponent implements OnInit {
    readonly #store = inject(Store);
    readonly #tokenStorage = inject(TokenStorageService);
    readonly #messageService = inject(MessageService);
    readonly #notificationWsService = inject(NotificationWebSocketService);
    readonly #destroyRef = inject(DestroyRef);
    readonly #sessionActivity = inject(SessionActivityService);
    readonly #passwordSessions = inject(PasswordSessionService);
    // Loads the "Report a problem" widget for signed-in users (follows the auth state).
    protected readonly ticketsWidget = inject(TicketsWidgetService);
    readonly sidebar = inject(SidebarService);
    user$ = this.#store.select(AuthSelectors.selectUser);
    appInitialized$ = this.#store.select(AuthSelectors.selectAuthInitialized);

    isMobileMenuOpen = false;

    ngOnInit(): void {
        this.#sessionActivity.start();
        this.#passwordSessions.start();
        this.#notificationWsService.onNotificationReceived
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((notification: Notification) => {
                if (
                    notification.type !== NotificationType.DOCUMENT_PROCESSING_COMPLETED &&
                    notification.type !== NotificationType.DOCUMENT_PROCESSING_FAILED &&
                    notification.type !== NotificationType.DOCUMENT_PROCESSING_REJECTED &&
                    notification.type !== NotificationType.DOCUMENT_PROCESSING_STALLED
                ) {
                    return;
                }

                const entityType = notification.metadata?.entityType as string | undefined;
                const label =
                    entityType === 'APPROVED_CARRIER'
                        ? 'Approved Carrier'
                        : entityType === 'FINISHED_PRODUCT_SPECIFICATION'
                            ? 'Finished Product Specification'
                            : entityType === 'CIR'
                                ? 'CIR'
                                : entityType === 'SHIPMENT'
                                    ? 'Disposition'
                                    : 'MPD';

                if (notification.type === NotificationType.DOCUMENT_PROCESSING_COMPLETED) {
                    this.#messageService.add({
                        key: 'global',
                        severity: 'success',
                        summary: 'Document Processed',
                        detail: notification.message || `${label} processing completed successfully`,
                        life: 5000,
                    });
                    return;
                }

                if (notification.type === NotificationType.DOCUMENT_PROCESSING_STALLED) {
                    this.#messageService.add({
                        key: 'global',
                        severity: 'warn',
                        summary: 'Processing Delayed',
                        detail: notification.message || `${label} processing is taking longer than expected.`,
                        life: 7000,
                    });
                    return;
                }

                this.#messageService.add({
                    key: 'global',
                    severity: 'error',
                    summary: 'Processing Failed',
                    detail: notification.message || `${label} processing failed. Please try again.`,
                    life: 7000,
                });
            });

        const accessToken = this.#tokenStorage.getAccessToken();
        const refreshToken = this.#tokenStorage.getRefreshToken();

        if (accessToken && refreshToken) {
            this.#store.dispatch(
                AuthActions.refreshTokenSuccess({
                    accessToken,
                    refreshToken,
                }),
            );
        }

        window.addEventListener('error', (event) => {
            console.error('GLOBAL ERROR CAUGHT - ɵcmp error:', event.error);
            console.error('Stack trace:', event.error.stack);
            console.error('Event:', event);
        });

        window.addEventListener('unhandledrejection', (event) => {
            console.error('GLOBAL PROMISE REJECTION - ɵcmp error:', event.reason);
            console.error('Stack trace:', event.reason.stack);
            console.error('Event:', event);
        });

        this.#store.dispatch(AuthActions.initAuthFromStorage());

        this.user$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((user) => {
            const accessToken = this.#tokenStorage.getAccessToken();
            if (!user && accessToken) {
                this.#store.dispatch(AuthActions.loadUserProfile());
            } else if (!user && !accessToken) {
                this.#store.dispatch(AuthActions.appLoaded());
            }
        });

        this.#tokenStorage.tokens$
            .pipe(
                takeUntilDestroyed(this.#destroyRef),
                filter((tokens) => !!tokens.accessToken),
            )
            .subscribe((tokens) => {
                this.user$.pipe(take(1)).subscribe((user) => {
                    if (!user) {
                        this.#store.dispatch(AuthActions.loadUserProfile());
                    }
                });
            });
    }

    toggleMobileMenu(): void {
        this.isMobileMenuOpen = !this.isMobileMenuOpen;
    }

    closeMobileMenu(): void {
        this.isMobileMenuOpen = false;
    }

    protected readonly UserRole = UserRole;
}
